'use client';

import Link from 'next/link';
import { Fragment, useEffect } from 'react';
import type { LibraryConnection } from '@/lib/opds/types';
import { buildBreadcrumbs } from '@/lib/utils/breadcrumbs';
import { fetchOpdsFeed } from '@/lib/utils/fetch';
import { useFeedTitles } from '@/stores/feed-titles';

// Module-level guard: avoid duplicate background prefetches when several
// breadcrumb instances mount during a navigation transition.
const inFlight = new Set<string>();

/**
 * Renders an inline ancestor chain (clickable links separated by ›). Designed
 * to be embedded in the page Header next to the page title — the title acts
 * as the trailing breadcrumb, so this component never renders the leaf.
 */
export function Breadcrumbs({
  currentAbsoluteUrl,
  library,
  includeCurrent = false,
}: {
  currentAbsoluteUrl: string;
  library: LibraryConnection;
  /**
   * When true, currentAbsoluteUrl is rendered as the deepest clickable
   * ancestor (use on book detail pages, where the URL is the parent feed of
   * the book). When false (default), it represents the current page and is
   * omitted from the chain — the page heading is the trailing crumb.
   */
  includeCurrent?: boolean;
}) {
  const titles = useFeedTitles((s) => s.titles);
  let crumbs = buildBreadcrumbs(currentAbsoluteUrl, library, titles);
  if (!includeCurrent) crumbs = crumbs.slice(0, -1);

  useEffect(() => {
    const snapshot = useFeedTitles.getState();
    let chain = buildBreadcrumbs(currentAbsoluteUrl, library, snapshot.titles);
    if (!includeCurrent) chain = chain.slice(0, -1);
    const need = chain
      .filter(
        (c) =>
          c.absoluteUrl &&
          snapshot.titles[c.absoluteUrl] === undefined &&
          !inFlight.has(c.absoluteUrl)
      )
      .map((c) => c.absoluteUrl as string);

    for (const url of need) {
      inFlight.add(url);
      fetchOpdsFeed(url, library)
        .then((feed) => {
          if (feed?.title) useFeedTitles.getState().setTitle(url, feed.title);
        })
        .catch(() => {})
        .finally(() => inFlight.delete(url));
    }
  }, [currentAbsoluteUrl, library, includeCurrent]);

  if (crumbs.length === 0) return null;

  return (
    <>
      {crumbs.map((c, i) => (
        <Fragment key={`${c.href}-${i}`}>
          <Link
            href={c.href}
            className="hidden max-w-[16ch] shrink-0 truncate text-neutral-500 hover:text-blue-600 hover:underline sm:inline-block"
          >
            {c.label}
          </Link>
          <span
            aria-hidden
            className="hidden shrink-0 text-neutral-300 sm:inline-block"
          >
            ›
          </span>
        </Fragment>
      ))}
    </>
  );
}
